import React from "react";
import {MarkdownText, RichTextField} from "~/components/MarkdownEditor/MarkdownEditor";
import {Chip, type AutocompleteRenderGetTagProps} from "@mui/material";
import type {Initiative} from "../../ApiTypes";
import {traffic_light} from "../../ApiTypes";
import {complexityTierLabel, stageGateLabel} from "../../sitLabels";
import i18n from "../../../i18n/i18n";

/**
 * SIT design-system atoms ported from the Claude Design export (Mithra 4.0).
 * They render the prototype's markup/classes; styling lives in sitDesign.scss
 * (scoped under `.sit-design`). Labels come from i18n so locale still applies.
 */

// internal DOI 1..5 → NExO N-code
const DOI_NCODE = ['N4', 'N3', 'N2', 'N1', 'N0'];
const FAROL_HUE: Record<number, string> = {1: 'bad', 2: 'warn', 3: 'info'};
const TRAFFIC_HUE: Record<string, string> = {green: 'good', yellow: 'warn', red: 'bad', gray: 'muted'};

// Design's stage-gate palette (Mithra 3.0 "By stage gate" chart) — one distinct
// colour per N level (N3 ≠ N2). Shared between the DOI pill here and the
// indicators dashboard's stage-gate bars so both stay visually identical.
export const DOI_N_COLOR: Record<string, string> = {
    N4: 'rgb(122,127,138)',
    N3: 'rgb(66,153,224)',
    N2: 'rgb(25,118,210)',
    N1: 'rgb(8,43,91)',
    N0: 'rgb(34,158,92)',
};

const nCode = (doi: number) => DOI_NCODE[doi - 1] ?? String(doi);
// Strip a leading "Nx - " so the pill shows the code bubble + the name only
const stageName = (doi: number) => stageGateLabel(doi).replace(/^N\d\s*[-–]\s*/, '');

export const SitDoiPill: React.FC<{doi: number; approvalState?: string | null; deactivated?: boolean}> =
    ({doi, approvalState, deactivated}) => {
        if (deactivated)
            return <span className="doi-pill hue-muted"><span className="doi-num">—</span>{i18n.t('pill.deactivated', {ns: 'sit'})}</span>;
        let hue: string | null = null;
        let label = stageName(doi);
        if (doi === 5 && approvalState === 'pending') {
            hue = 'warn';
            label = i18n.t('pill.pending', {ns: 'sit'});
        }
        if (doi === 5 && approvalState === 'approved') {
            hue = 'good';
            label = i18n.t('pill.ebit', {ns: 'sit'});
        }
        return (
            <span className={'doi-pill' + (hue ? ' hue-' + hue : '')}
                  style={hue ? undefined : {'--hc': DOI_N_COLOR[nCode(doi)]} as React.CSSProperties}>
                <span className="doi-num">{nCode(doi)}</span>{label}
                {approvalState === 'rejected' && <span className="doi-rej">{i18n.t('pill.rejected', {ns: 'sit'})}</span>}
            </span>
        );
    };

/** The stage gates; with `onSelect`, a stage is set by clicking it. */
export const SitDoiStepper: React.FC<{doi: number; approvalState?: string | null; onSelect?: (stage: number) => void}> =
    ({doi, approvalState, onSelect}) => (
        <div className={'doi-stepper' + (onSelect ? ' selectable' : '')}>
            {[1, 2, 3, 4, 5].map((stage) => {
                const state = stage < doi ? 'done' : stage === doi ? 'current' : 'todo';
                const pending = stage === 5 && doi === 5 && approvalState === 'pending';
                const content = <>
                    <div className="doi-step-dot">{stage < doi ? '✓' : nCode(stage)}</div>
                    <div className="doi-step-label">{stageName(stage)}</div>
                </>;
                const cls = 'doi-step ' + state + (pending ? ' pending' : '');
                return onSelect
                    ? <button key={stage} type="button" className={cls} aria-pressed={stage === doi}
                              title={i18n.t('form.setStage', {ns: 'sit', stage: stageName(stage)})}
                              onClick={() => onSelect(stage)}>{content}</button>
                    : <div key={stage} className={cls}>{content}</div>;
            })}
        </div>
    );

export const SitFarolPill: React.FC<{tier?: number | null; sm?: boolean}> = ({tier, sm}) => {
    if (!tier) return null;
    return (
        <span className={'farol-pill hue-' + (FAROL_HUE[tier] ?? 'info') + (sm ? ' sm' : '')}>
            <span className="farol-dot"/>{complexityTierLabel(tier)}
        </span>
    );
};

export const SitTrafficDot: React.FC<{initiative: Initiative; withLabel?: boolean}> = ({initiative, withLabel}) => {
    const [color, label] = traffic_light(initiative);
    return (
        <span className={'traffic hue-' + (TRAFFIC_HUE[color] ?? 'muted')} title={label}>
            <span className="traffic-dot"/>{withLabel && <span className="traffic-label">{label}</span>}
        </span>
    );
};

export const SitHelp: React.FC<{text: string}> = ({text}) => (
    <span className="sit-help" tabIndex={0}>
        <span className="sit-help-q">i</span>
        <span className="sit-help-bub">{text}</span>
    </span>
);

/**
 * `renderTags` for multi-select Autocompletes: shows a single "N selected"
 * summary instead of per-item chips (design's summary-count pattern). Selected
 * items live in the dropdown (checked), not as chips in the input.
 */
export function sitCountTags<T>(value: readonly T[]): React.ReactNode {
    return value.length ? <span className="sit-select-count">{i18n.t('filters.selected', {ns: 'sit', count: value.length})}</span> : null;
}

/**
 * `renderTags` for FORM multi-selects: a single selection shows its chip; two or
 * more collapse to the "N selected" summary. (Filters keep `sitCountTags`, which
 * always summarises.) Pass the same label accessor used for `getOptionLabel`.
 */
export function sitFormTags<T>(getLabel: (option: T) => string) {
    return (value: readonly T[], getTagProps: AutocompleteRenderGetTagProps): React.ReactNode => {
        if (value.length === 1) {
            const {key, ...tagProps} = getTagProps({index: 0});
            return <Chip key={key} size="small" label={getLabel(value[0])} {...tagProps}/>;
        }
        return value.length ? <span className="sit-select-count">{i18n.t('filters.selected', {ns: 'sit', count: value.length})}</span> : null;
    };
}

export const SitSection: React.FC<{
    n?: React.ReactNode; icon?: React.ReactNode; title: React.ReactNode; sub?: React.ReactNode;
    tint?: 'a' | 'b' | 'c' | 'plain'; right?: React.ReactNode; children?: React.ReactNode; id?: string;
}> = ({n, icon, title, sub, tint, right, children, id}) => (
    <section id={id} className={'sit-sec tint-' + (tint || 'plain')}>
        <header className="sit-sec-head">
            {(icon ?? n) != null && <span className="sit-sec-n">{icon ?? n}</span>}
            <div style={{flex: 1, minWidth: 0}}>
                <h3>{title}</h3>
                {sub && <div className="sit-sec-sub">{sub}</div>}
            </div>
            {right}
        </header>
        <div className="sit-sec-body">{children}</div>
    </section>
);

export const SitField: React.FC<{
    label: React.ReactNode; help?: string; req?: boolean; units?: React.ReactNode;
    full?: boolean; children?: React.ReactNode;
}> = ({label, help, req, units, full, children}) => (
    <label className={'sitf' + (full ? ' full' : '')}>
        <span className="sitf-label">{label}{req && <span className="sitf-req">*</span>}{help && <SitHelp text={help}/>}</span>
        <span className="sitf-input">{children}{units && <span className="sitf-units">{units}</span>}</span>
    </label>
);

/**
 * A long text with formatting (bold, italic, lists, links), stored as Markdown. It grows with its content; read-only it
 * shows the formatted text. `resetKey` changes when another record loads, as the editor keeps its own state.
 */
export const SitRichText: React.FC<{
    label: string; value: string; onChange: (markdown: string) => void; disabled?: boolean; resetKey: React.Key;
    placeholder?: string;
}> = ({label, value, onChange, disabled, resetKey, placeholder}) => disabled
    ? <div className="sit-richtext-read">{value ? <MarkdownText markdown={value}/> : <span className="sit-muted">—</span>}</div>
    : <RichTextField key={resetKey} initialMarkdown={value} onChange={onChange} ariaLabel={label} placeholder={placeholder}/>;

export const SitRead: React.FC<{
    label: React.ReactNode; help?: string; value: React.ReactNode; tone?: string;
}> = ({label, help, value, tone}) => (
    <div className="sitf read">
        <span className="sitf-label">{label}{help && <SitHelp text={help}/>}</span>
        <span className={'sitf-readval' + (tone ? ' tone-' + tone : '')}>{value}</span>
    </div>
);
